// frontend/src/pages/superAdmin/ManageUsers.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';

const ManageUsers = ({ user, setUser }) => {
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState('create');
  const [currentUser, setCurrentUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    role: 'admin',
    school_id: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const usersResponse = await api.get('/users');
        setUsers(usersResponse.data);
        
        const schoolsResponse = await api.get('/schools');
        setSchools(schoolsResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถโหลดข้อมูลได้'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      name: '',
      email: '',
      phone: '',
      address: '',
      role: 'admin',
      school_id: ''
    });
    setCurrentUser(null);
    setModalMode('create');
  };

  const openCreateModal = () => {
    resetForm();
    setModalMode('create');
  };

  const openEditModal = (userData) => {
    setCurrentUser(userData);
    setFormData({
      username: userData.username,
      password: '',
      name: userData.name,
      email: userData.email || '',
      phone: userData.phone || '',
      address: userData.address || '',
      role: userData.role,
      school_id: userData.school_id || ''
    });
    setModalMode('edit');
  };

  const openResetPasswordModal = (userData) => {
    setCurrentUser(userData);
    setFormData({
      ...formData,
      password: ''
    });
    setModalMode('resetPassword');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Validate form
      if (!formData.username || !formData.password || !formData.name || !formData.role) {
        Swal.fire({
          icon: 'error',
          title: 'กรุณากรอกข้อมูลให้ครบถ้วน',
          text: 'ชื่อผู้ใช้ รหัสผ่าน ชื่อ-นามสกุล และบทบาท เป็นข้อมูลที่จำเป็น'
        });
        setLoading(false);
        return;
      }
      
      // Validate school_id for admin role
      if (formData.role === 'admin' && !formData.school_id) {
        Swal.fire({
          icon: 'error',
          title: 'กรุณาเลือกโรงเรียน',
          text: 'โรงเรียนเป็นข้อมูลที่จำเป็นสำหรับผู้ดูแลโรงเรียน'
        });
        setLoading(false);
        return;
      }
      
      const response = await api.post('/users', formData);
      
      // Reload users
      const usersResponse = await api.get('/users');
      setUsers(usersResponse.data);
      
      // Close modal
      document.getElementById('closeUserModal').click();
      
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ',
        text: 'เพิ่มผู้ใช้งานเรียบร้อยแล้ว'
      });
    } catch (error) {
      console.error('Error creating user:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถเพิ่มผู้ใช้งานได้'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Validate form
      if (!formData.name) {
        Swal.fire({
          icon: 'error',
          title: 'กรุณากรอกข้อมูลให้ครบถ้วน',
          text: 'ชื่อ-นามสกุล เป็นข้อมูลที่จำเป็น'
        });
        setLoading(false);
        return;
      }
      
      const response = await api.put(`/users/${currentUser.id}`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address
      });
      
      // Reload users
      const usersResponse = await api.get('/users');
      setUsers(usersResponse.data);
      
      // Close modal
      document.getElementById('closeUserModal').click();
      
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ',
        text: 'อัปเดตข้อมูลผู้ใช้งานเรียบร้อยแล้ว'
      });
    } catch (error) {
      console.error('Error updating user:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถอัปเดตข้อมูลผู้ใช้งานได้'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Validate form
      if (!formData.password) {
        Swal.fire({
          icon: 'error',
          title: 'กรุณากรอกรหัสผ่านใหม่',
          text: 'รหัสผ่านเป็นข้อมูลที่จำเป็น'
        });
        setLoading(false);
        return;
      }
      
      const response = await api.put(`/users/${currentUser.id}/reset-password`, {
        newPassword: formData.password
      });
      
      // Close modal
      document.getElementById('closeUserModal').click();
      
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ',
        text: 'รีเซ็ตรหัสผ่านเรียบร้อยแล้ว'
      });
    } catch (error) {
      console.error('Error resetting password:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถรีเซ็ตรหัสผ่านได้'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (userId, currentActive) => {
    try {
      const result = await Swal.fire({
        icon: 'question',
        title: `${currentActive ? 'ปิดการใช้งาน' : 'เปิดการใช้งาน'}ผู้ใช้งาน`,
        text: `คุณต้องการ${currentActive ? 'ปิด' : 'เปิด'}การใช้งานผู้ใช้งานนี้ใช่หรือไม่?`,
        showCancelButton: true,
        confirmButtonText: 'ใช่',
        cancelButtonText: 'ไม่ใช่'
      });
      
      if (result.isConfirmed) {
        setLoading(true);
        
        const response = await api.put(`/users/${userId}/toggle-active`, {
          active: !currentActive
        });
        
        // Reload users
        const usersResponse = await api.get('/users');
        setUsers(usersResponse.data);
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: `${currentActive ? 'ปิด' : 'เปิด'}การใช้งานผู้ใช้งานเรียบร้อยแล้ว`
        });
      }
    } catch (error) {
      console.error('Error toggling user active:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || `ไม่สามารถ${currentActive ? 'ปิด' : 'เปิด'}การใช้งานผู้ใช้งานได้`
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'super_admin':
        return 'ผู้ดูแลระบบ';
      case 'admin':
        return 'ผู้ดูแลโรงเรียน';
      case 'referee':
        return 'กรรมการประเมิน';
      default:
        return role;
    }
  };

  return (
    <div className="wrapper">
      <Header user={user} title="จัดการผู้ใช้งาน" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">จัดการผู้ใช้งาน</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item"><Link to="/super-admin/dashboard">หน้าหลัก</Link></li>
                  <li className="breadcrumb-item active">จัดการผู้ใช้งาน</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">รายการผู้ใช้งานทั้งหมด</h3>
                <div className="card-tools">
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    data-bs-toggle="modal" 
                    data-bs-target="#userModal"
                    onClick={openCreateModal}
                  >
                    <i className="fas fa-plus"></i> เพิ่มผู้ใช้งาน
                  </button>
                </div>
              </div>
              <div className="card-body">
                {loading ? (
                  <div className="text-center my-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">กำลังโหลด...</span>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr>
                          <th style={{ width: '5%' }}>#</th>
                          <th>ชื่อผู้ใช้</th>
                          <th>ชื่อ-นามสกุล</th>
                          <th>อีเมล</th>
                          <th>บทบาท</th>
                          <th>โรงเรียน</th>
                          <th>สถานะ</th>
                          <th style={{ width: '15%' }}>จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.length > 0 ? (
                          users.map((userData, index) => (
                            <tr key={userData.id}>
                              <td>{index + 1}</td>
                              <td>{userData.username}</td>
                              <td>{userData.name}</td>
                              <td>{userData.email || '-'}</td>
                              <td>{getRoleName(userData.role)}</td>
                              <td>{userData.school_name || '-'}</td>
                              <td>
                                <span className={`badge ${userData.active ? 'bg-success' : 'bg-danger'}`}>
                                  {userData.active ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
                                </span>
                              </td>
                              <td>
                                <button 
                                  className="btn btn-info btn-sm me-1" 
                                  data-bs-toggle="modal" 
                                  data-bs-target="#userModal"
                                  onClick={() => openEditModal(userData)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button 
                                  className="btn btn-warning btn-sm me-1"
                                  data-bs-toggle="modal" 
                                  data-bs-target="#userModal"
                                  onClick={() => openResetPasswordModal(userData)}
                                >
                                  <i className="fas fa-key"></i>
                                </button>
                                <button 
                                  className={`btn ${userData.active ? 'btn-danger' : 'btn-success'} btn-sm`}
                                  onClick={() => handleToggleActive(userData.id, userData.active)}
                                >
                                  <i className={`fas ${userData.active ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center">ไม่พบข้อมูลผู้ใช้งาน</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* User Modal */}
      <div className="modal fade" id="userModal" tabIndex="-1" aria-labelledby="userModalLabel" aria-hidden="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="userModalLabel">
                {modalMode === 'create' ? 'เพิ่มผู้ใช้งาน' : 
                 modalMode === 'edit' ? 'แก้ไขข้อมูลผู้ใช้งาน' : 
                 'รีเซ็ตรหัสผ่าน'}
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                data-bs-dismiss="modal" 
                aria-label="Close"
                id="closeUserModal"
              ></button>
            </div>
            <div className="modal-body">
              {modalMode === 'create' && (
                <form onSubmit={handleCreate}>
                  <div className="mb-3">
                    <label htmlFor="username" className="form-label">ชื่อผู้ใช้ <span className="text-danger">*</span></label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="username" 
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">รหัสผ่าน <span className="text-danger">*</span></label>
                    <input 
                      type="password" 
                      className="form-control" 
                      id="password" 
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">ชื่อ-นามสกุล <span className="text-danger">*</span></label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="name" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">อีเมล</label>
                    <input 
                      type="email" 
                      className="form-control" 
                      id="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="phone" className="form-label">เบอร์โทรศัพท์</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="phone" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="address" className="form-label">ที่อยู่</label>
                    <textarea 
                      className="form-control" 
                      id="address" 
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows="2"
                    ></textarea>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="role" className="form-label">บทบาท <span className="text-danger">*</span></label>
                    <select 
                      className="form-select" 
                      id="role" 
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="super_admin">ผู้ดูแลระบบ</option>
                      <option value="admin">ผู้ดูแลโรงเรียน</option>
                      <option value="referee">กรรมการประเมิน</option>
                    </select>
                  </div>
                  {formData.role === 'admin' && (
                    <div className="mb-3">
                      <label htmlFor="school_id" className="form-label">โรงเรียน <span className="text-danger">*</span></label>
                      <select 
                        className="form-select" 
                        id="school_id" 
                        name="school_id"
                        value={formData.school_id}
                        onChange={handleInputChange}
                        required={formData.role === 'admin'}
                      >
                        <option value="">เลือกโรงเรียน</option>
                        {schools.map(school => (
                          <option key={school.id} value={school.id}>{school.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="text-end">
                    <button type="button" className="btn btn-secondary me-2" data-bs-dismiss="modal">ยกเลิก</button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          กำลังบันทึก...
                        </>
                      ) : 'บันทึก'}
                    </button>
                  </div>
                </form>
              )}

              {modalMode === 'edit' && (
                <form onSubmit={handleUpdate}>
                  <div className="mb-3">
                    <label htmlFor="username" className="form-label">ชื่อผู้ใช้</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="username" 
                      value={formData.username}
                      disabled
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">ชื่อ-นามสกุล <span className="text-danger">*</span></label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="name" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">อีเมล</label>
                    <input 
                      type="email" 
                      className="form-control" 
                      id="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="phone" className="form-label">เบอร์โทรศัพท์</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="phone" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="address" className="form-label">ที่อยู่</label>
                    <textarea 
                      className="form-control" 
                      id="address" 
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows="2"
                    ></textarea>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="role" className="form-label">บทบาท</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="role" 
                      value={getRoleName(formData.role)}
                      disabled
                    />
                  </div>
                  {formData.role === 'admin' && (
                    <div className="mb-3">
                      <label htmlFor="school" className="form-label">โรงเรียน</label>
                      <select 
                        className="form-select" 
                        id="school" 
                        disabled
                      >
                        {schools.map(school => (
                          <option 
                            key={school.id} 
                            value={school.id}
                            selected={school.id === formData.school_id}
                          >
                            {school.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="text-end">
                    <button type="button" className="btn btn-secondary me-2" data-bs-dismiss="modal">ยกเลิก</button>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          กำลังบันทึก...
                        </>
                      ) : 'บันทึก'}
                    </button>
                  </div>
                </form>
              )}

              {modalMode === 'resetPassword' && (
                <form onSubmit={handleResetPassword}>
                  <div className="mb-3">
                    <label htmlFor="username" className="form-label">ชื่อผู้ใช้</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="username" 
                      value={formData.username}
                      disabled
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">ชื่อ-นามสกุล</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      id="name" 
                      value={formData.name}
                      disabled
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="newPassword" className="form-label">รหัสผ่านใหม่ <span className="text-danger">*</span></label>
                    <input 
                      type="password" 
                      className="form-control" 
                      id="newPassword" 
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="text-end">
                    <button type="button" className="btn btn-secondary me-2" data-bs-dismiss="modal">ยกเลิก</button>
                    <button type="submit" className="btn btn-warning" disabled={loading}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          กำลังบันทึก...
                        </>
                      ) : 'รีเซ็ตรหัสผ่าน'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ManageUsers;